import { cn } from "./cn";

/**
 * An agent, rendered as their initials in a circle.
 *
 * Initials rather than the Google profile photo, and rather than the full name:
 * the list is a dense table, a name column of varying width is hard to scan
 * down, and a mix of photos and letter fallbacks is worse than either alone.
 * The full name is always in the tooltip and the accessible label, because two
 * agents can share initials and the circle alone must never be the only way to
 * tell them apart.
 */

interface Agent {
  id: number;
  name: string | null;
  email?: string | null;
  /** Their chosen pale tint, overriding the one derived from their id. */
  color?: string | null;
}

interface AvatarProps {
  /** null renders the unassigned placeholder. */
  agent: Agent | null;
  size?: keyof typeof SIZES;
  className?: string;
}

const SIZES = {
  xs: "h-5 w-5 text-[9px]",
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
} as const;

/**
 * Lane's palette, reused so the two products tint from the same set.
 *
 * A colour per agent rather than one shared brand tint: in a queue where most
 * rows are assigned, identical circles reduce the column to eight rows of the
 * same pink dot and the initials have to be read one at a time. Keyed off the
 * user id so an agent's colour never changes.
 */
const TINTS = [
  { bg: "#fce7f2", fg: "#b91562" }, // brand
  { bg: "#fee2e2", fg: "#b91c1c" },
  { bg: "#ffedd5", fg: "#c2410c" },
  { bg: "#fef3c7", fg: "#b45309" },
  { bg: "#dcfce7", fg: "#15803d" },
  { bg: "#ccfbf1", fg: "#0f766e" },
  { bg: "#dbeafe", fg: "#1d4ed8" },
  { bg: "#e0e7ff", fg: "#4338ca" },
  { bg: "#f3e8ff", fg: "#7e22ce" },
  { bg: "#e2e8f0", fg: "#475569" },
] as const;

/** "Jade Ruysseveldt" -> "JR". Matches Lane's Avatar so initials agree. */
export function initialsFor(agent: Agent): string {
  const source = agent.name?.trim() || agent.email || "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function Avatar({ agent, size = "sm", className }: AvatarProps) {
  if (!agent) {
    return (
      <span
        title="Unassigned"
        aria-label="Unassigned"
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full",
          "border border-dashed border-line",
          SIZES[size],
          className,
        )}
      />
    );
  }

  const label = agent.name ?? agent.email ?? `Agent ${agent.id}`;
  const tint = TINTS[agent.id % TINTS.length]!;

  /*
   * Chosen colours are pale, matching the derived ones, so the ink is looked up
   * from the same table rather than assumed. Falling back to the derived ink
   * keeps a colour saved before this change readable instead of white-on-pale.
   */
  const picked = agent.color
    ? (TINTS.find((entry) => entry.bg === agent.color) ?? {
        bg: agent.color,
        fg: tint.fg,
      })
    : tint;

  return (
    <span
      title={label}
      aria-label={label}
      style={{ backgroundColor: picked.bg, color: picked.fg }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-semibold",
        // Fixed-width digits keep two-letter circles optically identical.
        "tabular-nums",
        SIZES[size],
        className,
      )}
    >
      {initialsFor(agent)}
    </span>
  );
}
